import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { formatPrice } from '../../../../modules/currency';
import { BuyButton } from './BuyButton';
import { QuantityStepper } from './QuantityStepper';

export interface BottomActionProps {
  quantity: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  /** Per-unit price (number like 25 or string like "25.0"). */
  unitPrice: number | string;
  /** Minimum / maximum quantity the user may pick. */
  minQty?: number;
  maxQty?: number;
  /** Pack not purchasable (e.g. "Restocking") — the dock stays, locked, and
   *  the button reads this label. */
  unavailableLabel?: string;
  /** The balance doesn't cover the picked quantity: the button reads
   *  "Deposit to rip" (the stepper stays live — fewer packs may fit). */
  isShortOfFunds?: boolean;
  /** The Max Pull block, docked above the buy row on mobile so it is always
   *  in view (partner feedback 2026-10-07). Desktop shows it in the sidebar. */
  mobileStats: ReactNode;
}

interface BarProps {
  quantity: number;
  minQty: number;
  maxQty: number;
  onQuantityChange: (next: number) => void;
  onBuy: () => void;
  priceLabel: string;
  disabled: boolean;
  /** Nothing to buy — the button reads `lockedLabel` instead of the price and
   *  the stepper locks. */
  lockedLabel: string | null;
  isShortOfFunds: boolean;
}

// Same `--co-BottomActionShell-*` knobs as modern. Sleek's fallback fill is
// transparent: the dock shows the page background itself, so it can't drift
// from a tinted backdrop (partner Figma 2026-10-08: "make the color of the
// dock the same color as the background"); only the hairline marks it.
const SHELL_BG =
  'linear-gradient(' +
  'var(--co-BottomActionShell-gradientDirection, to bottom), ' +
  'var(--co-BottomActionShell-gradientFrom, transparent), ' +
  'var(--co-BottomActionShell-gradientTo, transparent))';
const SHELL_BORDER = 'var(--co-BottomActionShell-border, rgba(255,255,255,0.03))';

// Host-app integration knobs, as in modern. `marginBottom` lifts the whole
// dock off the viewport bottom; `paddingBottom` keeps it glued to the edge
// but extends the fill below the controls. `max(0px, …)` clamps a negative
// operator value, which would otherwise push the dock off-screen.
const SHELL_MARGIN_BOTTOM = 'max(0px, var(--co-BottomActionShell-marginBottom, 0px))';
const SHELL_PADDING_BOTTOM = 'max(0px, var(--co-BottomActionShell-paddingBottom, 0px))';

const SHELL_SHADOW =
  'drop-shadow(0 ' +
  'var(--co-BottomActionShell-shadowOffsetY, -6px) ' +
  'var(--co-BottomActionShell-shadowBlur, 20px) ' +
  'var(--co-BottomActionShell-shadowColor, transparent))';

/** Fill below the controls: the host's bottom inset plus the operator's
 *  padding, so the dock paints BEHIND a host tab bar instead of stopping
 *  above it and leaving a strip of page background. */
const SHELL_FILL_BELOW = `calc(var(--page-safe-bottom, 0px) + ${SHELL_PADDING_BOTTOM})`;

/** Stepper / Buy height — one knob for both, so they never drift apart
 *  again (partner Loom 2026-10-14: both 52). */
const DOCK_BUTTON_HEIGHT = 'var(--co-BottomActionShell-buttonHeight, 52px)';
/** Height of the controls row: 16px top padding + the buttons + 16px. */
const MOBILE_DOCK_HEIGHT = `calc(32px + ${DOCK_BUTTON_HEIGHT})`;
/** Height of the docked Max Pull block: 16px + 20px label + 4px + 44px
 *  figures + 20px. Set on the block itself, so the spacer always matches. */
const MOBILE_STATS_HEIGHT_PX = 104;

/** In-flow spacer reserving room for the portal'd mobile dock — the Max Pull
 *  block and the controls row (consumed by PackLayout). The host inset is not
 *  added: `#root` already carries it, and the inset extends the dock
 *  downward, so its top edge does not move. */
export const MOBILE_RAIL_SPACER_HEIGHT =
  `calc(${MOBILE_DOCK_HEIGHT} + ${MOBILE_STATS_HEIGHT_PX}px + ${SHELL_MARGIN_BOTTOM} + ${SHELL_PADDING_BOTTOM})`;

function DockRow({
  quantity,
  minQty,
  maxQty,
  onQuantityChange,
  onBuy,
  priceLabel,
  disabled,
  lockedLabel,
  isShortOfFunds,
}: BarProps) {
  return (
    <div className="flex items-center gap-2.5">
      <QuantityStepper
        value={quantity}
        min={minQty}
        max={maxQty}
        onChange={onQuantityChange}
        size="md"
        disabled={lockedLabel !== null}
      />
      <BuyButton
        onClick={onBuy}
        count={quantity}
        priceLabel={priceLabel}
        disabled={disabled}
        lockedLabel={lockedLabel}
        isShortOfFunds={isShortOfFunds}
      />
    </div>
  );
}

/**
 * Mobile dock — edge-to-edge, fixed to the viewport bottom, portalled to
 * <body> so no ancestor `transform` / `filter` / `contain` can hijack its
 * containing block.
 *
 * The fixed wrapper is its own `@container`: a `fixed left-0 right-0`
 * element spans the viewport EXCLUDING a classic scrollbar — the width the
 * pack grid's `@[800px]` query measures — so this dock hides exactly when
 * the desktop one appears. `z-[45]` clears Backdrop's bottom inset cover
 * (z-40), which would otherwise repaint the fill band, and stays below the
 * dialog layer (z-50).
 */
function MobileBar({ stats, ...props }: BarProps & { stats: ReactNode }) {
  return createPortal(
    <div className="@container fixed left-0 right-0 z-[45]" style={{ bottom: SHELL_MARGIN_BOTTOM }}>
      <div className="@[800px]:hidden">
        {/* No fill of its own: the figures sit on the page background and
            blend with it (partner feedback 2026-10-07), and so does the buy
            row below the hairline. The lobby fits the screen (see the
            slider's height fit), so nothing scrolls underneath. */}
        <div className="flex flex-col justify-end" style={{ height: MOBILE_STATS_HEIGHT_PX }}>
          <div className="mx-auto w-full max-w-[1400px]">{stats}</div>
        </div>
        <div
          data-co-component="BottomActionShell"
          className="border-t border-solid"
          style={{ background: SHELL_BG, borderColor: SHELL_BORDER, filter: SHELL_SHADOW }}
        >
          <div className="mx-auto w-full max-w-[1400px] px-5 py-4">
            <DockRow {...props} />
          </div>
          <div aria-hidden style={{ height: SHELL_FILL_BELOW }} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Desktop dock — static, the same row inside the sidebar column. */
function DesktopBar(props: BarProps) {
  return (
    <div data-co-component="BottomActionShell" className="hidden @[800px]:block">
      <DockRow {...props} />
    </div>
  );
}

/**
 * Pack-page bottom action.
 *
 *   < 800px (mobile) — `fixed bottom-0`, edge-to-edge dock with the Max
 *                      Pull block on top of the buy row.
 *   ≥ 800px (desktop) — `static`, inside the sidebar.
 *
 * Both hide/show via `@[800px]` container queries measuring the SAME layout
 * width, so exactly one dock is visible at any width.
 */
export function BottomAction({
  quantity,
  onQuantityChange,
  onBuy,
  unitPrice,
  minQty = 1,
  maxQty = 99,
  unavailableLabel,
  isShortOfFunds = false,
  mobileStats,
}: BottomActionProps) {
  const unitAmount = Number(unitPrice) || 0;
  const totalAmount = unitAmount * Math.max(quantity, 1);
  const outOfStock = maxQty <= 0;
  const lockedLabel = unavailableLabel ?? (outOfStock ? 'Sold out' : null);

  const shared: BarProps = {
    quantity,
    minQty,
    maxQty,
    onQuantityChange,
    onBuy,
    priceLabel: formatPrice(totalAmount),
    disabled: lockedLabel !== null,
    lockedLabel,
    isShortOfFunds,
  };

  return (
    <>
      <MobileBar {...shared} stats={mobileStats} />
      <DesktopBar {...shared} />
    </>
  );
}
